import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Palette, Plus, PencilSimple, Trash, UploadSimple, Check, LockKey } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && !u.startsWith("http") ? `${API_URL}${u}` : u);

const EMPTY = { name: "", hero_image_url: "", hero_subtitle: "", sort: 0 };

export default function AdminDashboardTheme() {
  const [themes, setThemes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/dashboard-themes")
      .then(({ data }) => { setThemes(data.themes || []); setActiveId(data.active_id || null); })
      .catch(() => toast.error("Error al cargar temas"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setDialog("create"); };
  const openEdit = (t) => { setForm({ name: t.name, hero_image_url: t.hero_image_url, hero_subtitle: t.hero_subtitle || "", sort: t.sort || 0 }); setDialog({ mode: "edit", theme: t }); };
  const close = () => { setDialog(null); setForm(EMPTY); };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Máximo 10MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/dashboard-themes/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, hero_image_url: data.url }));
      toast.success("Imagen subida");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim() || !form.hero_image_url.trim()) { toast.error("Nombre e imagen obligatorios"); return; }
    setSaving(true);
    try {
      if (dialog === "create") await api.post("/admin/dashboard-themes", form);
      else await api.put(`/admin/dashboard-themes/${dialog.theme.id}`, form);
      toast.success("Guardado");
      close();
      load();
      window.dispatchEvent(new Event("dashboard-theme:changed"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (t.is_builtin) { toast.error("No puedes eliminar un tema predeterminado"); return; }
    if (!window.confirm(`¿Eliminar "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/dashboard-themes/${t.id}`);
      toast.success("Eliminado");
      load();
      window.dispatchEvent(new Event("dashboard-theme:changed"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const activate = async (t) => {
    try {
      await api.post(`/admin/dashboard-themes/${t.id}/activate`);
      toast.success(`"${t.name}" aplicado a todos los usuarios`);
      setActiveId(t.id);
      window.dispatchEvent(new Event("dashboard-theme:changed"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="admin-dashboard-theme-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00] flex items-center gap-1">
            <LockKey size={12} weight="fill" /> Solo admin · Afecta a todos los usuarios
          </p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter text-white">
            Temas del Dashboard
          </h1>
          <p className="text-sm text-gray-400 mt-1 max-w-2xl">
            Elige la imagen hero y el subtítulo que verán todos los usuarios al abrir su dashboard. Puedes crear nuevos temas o editar los existentes.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="dt-new-btn">
          <Plus size={14} className="mr-1" /> Nuevo tema
        </Button>
      </div>

      {loading ? (
        <p className="text-center text-gray-500 py-12">Cargando…</p>
      ) : themes.length === 0 ? (
        <p className="text-center text-gray-500 py-12">No hay temas disponibles</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dt-grid">
          {themes.map((t) => {
            const isActive = t.id === activeId;
            return (
              <div
                key={t.id}
                className={`card-tactical p-0 overflow-hidden border-2 transition-all ${isActive ? "border-[#CCFF00] shadow-[0_0_24px_rgba(204,255,0,0.3)]" : "border-white/10"}`}
                data-testid={`dt-card-${t.id}`}
              >
                <div className="relative aspect-[16/10] bg-[#0A0A0A] overflow-hidden">
                  <img src={absUrl(t.hero_image_url)} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                  {isActive && (
                    <span className="absolute top-2 left-2 px-2 py-1 rounded text-[9px] uppercase font-black bg-[#CCFF00] text-black tracking-widest flex items-center gap-1 shadow-lg">
                      <Check size={10} weight="bold" /> Activo
                    </span>
                  )}
                  {t.is_builtin && (
                    <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[8px] uppercase font-black bg-black/70 text-cyan-400 border border-cyan-500/30 tracking-widest">
                      Predeterminado
                    </span>
                  )}
                </div>
                <div className="p-3 space-y-1.5">
                  <h3 className="font-['Barlow_Condensed'] text-lg font-black uppercase tracking-tight text-white leading-tight">{t.name}</h3>
                  {t.hero_subtitle && <p className="text-xs text-gray-400 line-clamp-2">{t.hero_subtitle}</p>}
                  <div className="flex gap-1.5 pt-1.5 flex-wrap">
                    {!isActive && (
                      <Button onClick={() => activate(t)} size="sm" className="h-7 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-[9px]" data-testid={`dt-activate-${t.id}`}>
                        <Check size={10} className="mr-1" /> Aplicar a todos
                      </Button>
                    )}
                    <Button onClick={() => openEdit(t)} size="sm" variant="outline" className="h-7 border-white/15 text-white text-[9px] uppercase" data-testid={`dt-edit-${t.id}`}>
                      <PencilSimple size={10} className="mr-1" /> Editar
                    </Button>
                    {!t.is_builtin && (
                      <Button onClick={() => remove(t)} size="sm" variant="outline" className="h-7 border-red-500/30 text-red-400 text-[9px] uppercase" data-testid={`dt-delete-${t.id}`}>
                        <Trash size={10} className="mr-1" /> Borrar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Editor */}
      <Dialog open={!!dialog} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent className="bg-[#0A0A0A] border-[#CCFF00]/30 max-w-lg" data-testid="dt-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight">
              {dialog === "create" ? "Nuevo tema" : "Editar tema"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">Nombre del tema</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="p.ej. Verano Padel 2026" className="bg-black border-white/15 text-white h-9" data-testid="dt-form-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">Subtítulo del hero</Label>
              <Textarea value={form.hero_subtitle} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} placeholder="Tu deporte · Tu comunidad · Tu app" className="bg-black border-white/15 text-white min-h-[60px]" data-testid="dt-form-subtitle" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">Imagen hero</Label>
              <div className="flex gap-2 items-center">
                <Input value={form.hero_image_url} onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })} placeholder="URL o sube una..." className="bg-black border-white/15 text-white h-9 flex-1" data-testid="dt-form-url" />
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} size="sm" variant="outline" className="border-[#CCFF00]/30 text-[#CCFF00] h-9" data-testid="dt-form-upload">
                  <UploadSimple size={14} className="mr-1" /> {uploading ? "Subiendo…" : "Subir"}
                </Button>
              </div>
              {form.hero_image_url && (
                <img src={absUrl(form.hero_image_url)} alt="preview" className="mt-2 max-h-40 rounded border border-white/10 object-contain mx-auto" />
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">Orden</Label>
              <Input type="number" value={form.sort} onChange={(e) => setForm({ ...form, sort: parseInt(e.target.value || "0", 10) })} className="bg-black border-white/15 text-white h-9 w-32" data-testid="dt-form-sort" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={close} variant="outline" className="border-white/15">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold" data-testid="dt-form-save">
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
